import { Link, Navigate, useLocation, useParams, useOutletContext } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { usePageQueryParams } from '../../hooks/usePageQueryParams';
import schoolYearViewConfig from './SchoolYearView.config';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import type { AppLayoutContext } from '~/types';
import { schoolYearSettingsPath } from '../SchoolSettings/schoolYearSettingsPath';
import './SchoolYearView.css';

function SchoolYearView() {
  const { yearSlug } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const location = useLocation();
  usePageQueryParams(schoolYearViewConfig);

  if (!school) {
    return (
      <div className="p-6">
        <p>Loading...</p>
      </div>
    );
  }

  const schoolYear = school?.schoolYears?.find(
    (y: any) => y.slug === yearSlug
  );

  if (!schoolYear) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>School Year Not Found</CardTitle>
            <CardDescription>The school year you're looking for doesn't exist.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isAdmin = ['PRIMARY_ADMIN', 'ADMIN'].includes(roleContext.selectedRole ?? '');
  const settingsPath = schoolYearSettingsPath(school.slug, schoolYear.slug);

  if (isAdmin && location.hash === '#academic-terms') {
    return <Navigate replace to={settingsPath} />;
  }

  return (
    <div className="school-year-view p-6 space-y-4">
      <h2 className="text-3xl font-bold">{schoolYear.name}</h2>
      <Card>
        <CardHeader>
          <CardTitle>School Year Details</CardTitle>
          <CardDescription>Information about this school year</CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 font-semibold">Property</th>
                <th className="text-left p-2 font-semibold">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-2 text-muted-foreground">Name</td>
                <td className="p-2">{schoolYear.name}</td>
              </tr>
              <tr className="border-b">
                <td className="p-2 text-muted-foreground">Slug</td>
                <td className="p-2">{schoolYear.slug}</td>
              </tr>
              <tr className="border-b">
                <td className="p-2 text-muted-foreground">Start Date</td>
                <td className="p-2">{String(schoolYear.startDate)}</td>
              </tr>
              <tr className="border-b">
                <td className="p-2 text-muted-foreground">End Date</td>
                <td className="p-2">{String(schoolYear.endDate)}</td>
              </tr>
              <tr>
                <td className="p-2 text-muted-foreground">State</td>
                <td className="p-2">{schoolYear.state}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>School Year &amp; Academic Terms</CardTitle>
            <CardDescription>
              School-year creation and academic-term management now live in School Settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to={settingsPath}>
                <Settings className="mr-2 h-4 w-4" />
                Manage in School Settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default SchoolYearView;
